import { isSameDay } from "date-fns/isSameDay"
import { getDailyCurrencyRateList } from "../services/currency/currency.api"
import {
    isAfter,
    eachMonthOfInterval,
    format,
    endOfMonth,
    isSameMonth,
    subDays,
    isWeekend,
    nextFriday,
    isWithinInterval,
    startOfDay,
    endOfDay,
    startOfMonth,
} from "date-fns"
import { toZonedTime } from "date-fns-tz"
import pLimit from "p-limit"
import { addDbItem, getDbItem } from "../utils/idb-util"
import { delay } from "./utils"
import uniq from "lodash/uniq"
import { makeRateLookup } from "./fxLookup"

export async function getExchangeRates({ baseCurrency, currencies, startDate, endDate, dates }) {
    const currs = currencies ? uniq(currencies.filter((d) => d !== baseCurrency)) : undefined

    let yesterday = subDays(new Date(), 1)
    if (isWeekend(yesterday)) {
        yesterday = nextFriday(subDays(new Date(), 4))
    }

    // Parse and sanitize target dates if passed
    const parsedTargetDates = dates?.length
        ? dates.map(parseEpochOrDate).filter((d) => d && !isNaN(d.getTime()) && !isAfter(d, yesterday))
        : null

    let months = []
    let effectiveEndDate = endDate

    if (parsedTargetDates?.length) {
        // Mode A: Sparse dates -> Extract unique required months
        const uniqueMonthKeys = uniq(parsedTargetDates.map((d) => format(d, "yyyy-MM")))
        months = uniqueMonthKeys.map((key) => new Date(`${key}-01T00:00:00`))
    } else if (startDate) {
        // Mode B: Continuous date range
        if (isAfter(startDate, yesterday) || (endDate && isAfter(startDate, endDate))) {
            return []
        }
        effectiveEndDate = !endDate || isAfter(endDate, yesterday) ? yesterday : endDate
        months = eachMonthOfInterval({ start: startDate, end: effectiveEndDate })
    } else {
        return []
    }

    const cachedResults = []
    const missingMonths = []

    function getCacheKey(month) {
        let monthKey = `${baseCurrency}-${format(month, "yyyy-MM")}`
        if (currs && currs.length > 0) {
            monthKey += `-${[...currs].sort().join("-")}`
        }
        return monthKey
    }

    // 1. Parallel cache check for required target months ONLY
    await Promise.all(
        months.map(async (month) => {
            const isCurrentMonth = isSameMonth(month, yesterday)
            const monthKey = getCacheKey(month)

            if (!isCurrentMonth) {
                try {
                    const cf = await getDbItem("orgExchangeRates", "fxStore", monthKey)
                    if (cf?.exchangeRates?.length) {
                        cachedResults.push(...cf.exchangeRates)
                        return
                    }
                } catch (e) {
                    console.error("FX cache read failed", monthKey, e)
                }
            }

            missingMonths.push(month)
        }),
    )

    let serverFx = []

    // 2. Fetch uncached target months from server
    if (missingMonths.length > 0) {
        missingMonths.sort((a, b) => a.getTime() - b.getTime())

        const fetchPromises = missingMonths.map(async (month) => {
            const fetchStart = startOfMonth(month)
            const fetchEnd = isSameMonth(month, yesterday) ? yesterday : endOfMonth(month)

            try {
                return (
                    (await getDailyCurrencyRateList(baseCurrency, {
                        startDate: fetchStart,
                        endDate: fetchEnd,
                        currencies: currs,
                    })) || []
                )
            } catch (e) {
                console.error("FX server fetch failed for month", month, e)
                return []
            }
        })

        const fetchedChunks = await Promise.all(fetchPromises)
        serverFx = fetchedChunks.flat()

        // 3. Cache fetched months in the background (non-blocking)
        missingMonths.forEach(async (month) => {
            if (isSameMonth(month, yesterday)) return

            const monthKey = getCacheKey(month)
            const monthFx = serverFx.filter((item) => {
                const d = parseEpochOrDate(item?.day ?? item?.daya)
                return d && isSameMonth(d, month)
            })

            if (monthFx.length > 0) {
                try {
                    await addDbItem("orgExchangeRates", "fxStore", {
                        id: monthKey,
                        exchangeRates: monthFx,
                    })
                } catch (e) {
                    console.error("FX cache write failed", monthKey, e)
                }
            }
        })
    }

    // 4. Merge and deduplicate by YYYY-MM-DD
    const rateMap = new Map()
    ;[...cachedResults, ...serverFx].forEach((item) => {
        const rawDay = item?.day ?? item?.daya
        if (rawDay !== undefined && rawDay !== null) {
            try {
                const dateObj = parseEpochOrDate(rawDay)
                if (dateObj) {
                    const key = format(toZonedTime(dateObj), "yyyy-MM-dd")
                    rateMap.set(key, item)
                }
            } catch (e) {
                console.warn("Invalid FX item day format:", rawDay, e)
            }
        }
    })

    // 5. Filter output matching exact dates (including weekend lookbacks) or date range
    return filterAndFormatRates(
        Array.from(rateMap.values()),
        currencies,
        parsedTargetDates,
        startDate,
        effectiveEndDate,
    )
}

function parseEpochOrDate(rawDay) {
    if (rawDay === undefined || rawDay === null) return null
    if (typeof rawDay === "number") {
        return new Date(rawDay < 1e11 ? rawDay * 1000 : rawDay)
    }
    if (typeof rawDay === "string") {
        const num = Number(rawDay)
        if (!isNaN(num)) {
            return new Date(num < 1e11 ? num * 1000 : num)
        }
    }
    const d = new Date(rawDay)
    return isNaN(d.getTime()) ? null : d
}

function filterAndFormatRates(rates, currencies, parsedTargetDates, startDate, endDate) {
    let targetDaySet = null

    if (parsedTargetDates?.length) {
        targetDaySet = new Set()
        // Include target date AND preceding 3 days (to cover weekends & bank holidays)
        parsedTargetDates.forEach((d) => {
            const zoned = toZonedTime(d)
            targetDaySet.add(format(zoned, "yyyy-MM-dd"))
            targetDaySet.add(format(subDays(zoned, 1), "yyyy-MM-dd"))
            targetDaySet.add(format(subDays(zoned, 2), "yyyy-MM-dd"))
            targetDaySet.add(format(subDays(zoned, 3), "yyyy-MM-dd"))
        })
    }

    return rates
        .filter((c) => {
            const rawDay = c?.day ?? c?.daya
            const dateObj = parseEpochOrDate(rawDay)
            if (!dateObj) return false

            const zonedDay = toZonedTime(dateObj)
            const dayKey = format(zonedDay, "yyyy-MM-dd")

            if (targetDaySet) {
                return targetDaySet.has(dayKey)
            }

            if (startDate && endDate) {
                return isWithinInterval(zonedDay, {
                    start: startOfDay(startDate),
                    end: endOfDay(endDate),
                })
            }
            if (startDate) return isSameDay(zonedDay, startDate)
            if (endDate) return isSameDay(zonedDay, endDate)
            return true
        })
        .map((c) => {
            if (!currencies?.length) return c
            return {
                ...c,
                rates: Object.fromEntries(currencies.map((cc) => [cc, c.rates?.[cc]])),
            }
        })
}

//getFxForTheDay lives in utils/mf-utils.js - the duplicate that used to be here had no
//callers, no weekend handling, and fell back to an arbitrarily-ordered array entry

/**
 * The rate in effect on one day (an expense's): the latest published on or before it
 * (utils/fxLookup). A day with no rate of its own - today before tonight's run, a missed run -
 * takes the last one; it is never left unconverted while there's a rate in the month before.
 *
 * @return {Promise<number|null>} units of `currency` per 1 `baseCurrency` (divide by it), or null
 */
export async function getRateOnOrBefore({ baseCurrency, currency, day }) {
    if (!currency || currency === baseCurrency) return 1
    const date = toZonedTime(day)
    if (isNaN(date.getTime())) return null

    // Rates stop at yesterday: a day after that looks back from today
    const until = isAfter(date, new Date()) ? new Date() : date
    const rates = await getExchangeRates({
        baseCurrency,
        currencies: [currency],
        startDate: startOfDay(subDays(until, 31)),
        endDate: endOfDay(until),
    })

    return makeRateLookup(rates)(currency, date)
}

/**
 * getRateOnOrBefore for many days at once (a mission's expenses): one fetch per currency over
 * the days' span - from a month before the first, as each day looks back - not one per day.
 *
 * @param items {{ currency: string, day: * }[]}
 * @return {Promise<Function>} (currency, day) => the rate getRateOnOrBefore gives that day
 */
export async function getRatesOnOrBefore({ baseCurrency, items = [] }) {
    const now = new Date()
    const daysByCurrency = new Map()
    items.forEach(({ currency, day }) => {
        if (!currency || currency === baseCurrency) return
        const date = toZonedTime(day)
        if (isNaN(date.getTime())) return
        daysByCurrency.set(currency, [...(daysByCurrency.get(currency) || []), date])
    })

    const lookups = new Map()
    await Promise.all(
        [...daysByCurrency].map(async ([currency, dates]) => {
            // Rates stop at yesterday: a day after that looks back from today
            const capped = dates.map((d) => (isAfter(d, now) ? now : d))
            const first = new Date(Math.min(...capped))
            const last = new Date(Math.max(...capped))
            const rates = await getExchangeRates({
                baseCurrency,
                currencies: [currency],
                startDate: startOfDay(subDays(first, 31)),
                endDate: endOfDay(last),
            })
            lookups.set(currency, makeRateLookup(rates))
        }),
    )

    return (currency, day) => {
        if (!currency || currency === baseCurrency) return 1
        const date = toZonedTime(day)
        if (isNaN(date.getTime())) return null
        return lookups.get(currency)?.(currency, date) ?? null
    }
}

export async function grabExchangeRates({ missions, baseCurrency, startDate, endDate }) {
    if (!missions?.length) {
        return []
    }

    const hasAnotherCurrency = missions.some((m) => m.currency !== baseCurrency)

    if (hasAnotherCurrency) {
        const fxrRates = await getExchangeRates({ baseCurrency, startDate, endDate })

        return fxrRates
    }

    return []
}
